import React from 'react';
import { PropTypes } from 'prop-types';
import { get, toNumber, toString } from 'lodash';
import { RequiredValidation, Heading, Stack, Text, FlexRow, FlexCol } from 'sarsaparilla';

import {
    getClassName,
    isExitingMtWhitney,
} from 'ui-inyonwhitney/src/utils/inyoUtils';
import SmartSelector from 'ui-permit/src/components/SmartSelector';
import { PERMIT as permitProps } from 'ui-permit/src/props/permit';

import { useValidation } from '../hooks/useValidation';
import { TRAVEL_METHOD_TYPE_PROP_PATH } from '../constants/propPaths';

const defaultDataBindingMap = {
    [TRAVEL_METHOD_TYPE_PROP_PATH]: 'travel_method_type',
};

const defaultAttributes = {};

export default (
    issuanceShape,
    dataBindingsMap = defaultDataBindingMap,
    attributes = defaultAttributes
) => {
    const dm = { ...defaultDataBindingMap, ...dataBindingsMap };

    const propTypes = {
        reservation: issuanceShape,
        permit: permitProps,
        validation: PropTypes.shape({}).isRequired,
        pActions: PropTypes.shape({}).isRequired,
        displayValidationError: PropTypes.bool.isRequired,
        shouldValidateOnBEOnly: PropTypes.bool,
        name: PropTypes.string,
        addFieldRef: PropTypes.func,
    };

    function TravelMethod({
        name,
        pActions,
        permit,
        reservation,
        shouldValidateOnBEOnly,
        validation,
        displayValidationError,
        addFieldRef,
    }) {
        let travelMethod = toString(
            get(reservation, dm[TRAVEL_METHOD_TYPE_PROP_PATH], '0')
        );
        if (travelMethod === '0') travelMethod = '';

        const { isInvalid } = useValidation(
            { value: travelMethod, id: 'travel-method' },
            'Travel Method is required.',
            addFieldRef?.(name, 0)
        );

        const onFormValidate = (isInValid, error, path) => {
            if (isInValid) {
                pActions.formValidationChange(`${reservation.id}_${path}`, error);
            } else {
                pActions.formValidationChange(`${reservation.id}_${path}`, null);
            }
        };

        const getTravelMethods = () => {
            let methods = permit.travel_methods;
            if (methods) {
                if (isExitingMtWhitney(reservation)) {
                    // only include travel methods that do not involve stock
                    methods = methods.filter(
                        (method) => !method.name.toLowerCase().includes('stock')
                    );
                }
                return methods.map((method) => {
                    return { value: method.type.toString(), label: method.name };
                });
            }
            return [];
        };

        const handleChange = ({ target }) => {
            const { value } = target;
            pActions.updatePendingReservation(
                reservation.id,
                dm[TRAVEL_METHOD_TYPE_PROP_PATH],
                toNumber(value)
            );
            if (shouldValidateOnBEOnly)
                pActions.formValidationChange(dm[TRAVEL_METHOD_TYPE_PROP_PATH], null);
        };

        const className = shouldValidateOnBEOnly
            ? getClassName(get(validation, `${dm[TRAVEL_METHOD_TYPE_PROP_PATH]}`), true)
            : getClassName(
                  get(
                      validation,
                      `${reservation.id}_${dm[TRAVEL_METHOD_TYPE_PROP_PATH]}`
                  ),
                  displayValidationError
              );

        const hastValidationError =
            (shouldValidateOnBEOnly &&
                get(validation, dm[TRAVEL_METHOD_TYPE_PROP_PATH])) ||
            displayValidationError ||
            isInvalid;

        const validationError = !travelMethod && hastValidationError ? 'rec-error' : '';

        return (
            <Stack space="sm">
                <Heading headingLevel={2} appearance="h5">
                    Travel Method
                </Heading>
                <Text>
                    Please select the type of travel you will be using on this trip.
                </Text>
                <FlexRow>
                    <FlexCol xs={12} sm={8} md={6}>
                        <SmartSelector
                            isRequired
                            label="Travel Method"
                            className={className}
                            options={getTravelMethods()}
                            name="travel-method"
                            id="travel-method"
                            value={travelMethod}
                            handleChange={handleChange}
                            validationError={validationError}
                            includePlaceholderOption
                        />
                        {!shouldValidateOnBEOnly && (
                            <RequiredValidation
                                value={travelMethod}
                                displayInSummary
                                onValidated={(isInValid, error) =>
                                    onFormValidate(
                                        isInValid,
                                        error,
                                        dm[TRAVEL_METHOD_TYPE_PROP_PATH]
                                    )
                                }
                                controlLabel="Travel Method"
                            />
                        )}
                    </FlexCol>
                </FlexRow>
            </Stack>
        );
    }

    TravelMethod.propTypes = propTypes;
    TravelMethod.attributes = attributes;

    return TravelMethod;
};
